import {
  Button,
  Grid,
  Pagination,
  Typography,
  Box
} from '@mui/material';
import { SessionsListResponse, UserDevice } from './interface';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  expireUserSession,
  getUserSessionsAsync,
  updateUserSessionsAsync
} from '../../redux/slices/usersSlice';
import { RootState } from '../../redux/store';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { User } from '../../resources/types/usersTypes';

interface Props {
  user: User;
}

const UserSessionsContent = (props: Props) => {
  const { user } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isSuperAdmin = useSelector(
    (state: RootState) => state.auth.info?.type === 'superAdmin'
  );

  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [data, setData] = useState<UserDevice[]>([]);

  const getUserSessions = () => {
    if (!user?._id) return;
    getUserSessionsAsync({
      userId: user?._id,
      page,
      limit: 20
    }).then((res: SessionsListResponse) => {
      const devices = res.data.userDevices ?? [];
      devices.sort(
        (a: UserDevice, b: UserDevice) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setData(devices);
      setPages(res.pagination.totalPages ?? 1);
    });
  };

  const updateUserSessions = (id: string, banned: boolean) => {
    if (!user?._id) return;
    updateUserSessionsAsync(id, { banned }).then(
      (res: SessionsListResponse) => {
        getUserSessions();
      }
    );
  };

  useEffect(() => {
    getUserSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, user?._id]);

  const onCloseSessions = () => {
    // eslint-disable-next-line no-restricted-globals
    const confirmation = confirm(
      t('userModal.confirmEndAllSessions')
    );
    if (confirmation && user._id) {
      dispatch(expireUserSession(user._id));
      alert(t('userModal.sessionsEndedSuccess'));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {t('userModal.userLabel')} {user?.firstName} {user?.lastName} {user?.phone}
      </Typography>
      <Grid
        container
        spacing={2}
        direction='column'
        minWidth={400}
        paddingX={1}
      >
        <Grid item>
          {data.length === 0 ? (
            <Grid item justifyContent={'center'} alignItems={'center'}>
              <Typography textAlign={'center'}>{t('userModal.noSessions')}</Typography>
            </Grid>
          ) : null}
          {data.map((item, index) => (
            <Grid
              key={index}
              container
              flexDirection={'row'}
              justifyContent='space-between'
              marginTop={1}
            >
              <Grid
                item
                xs={isSuperAdmin ? 5 : 8}
                container
                flexDirection={'column'}
              >
                <Typography fontWeight={'600'}>{item.deviceInfo}</Typography>
                <Typography>{item.simCardNumber}</Typography>
              </Grid>
              <Grid
                item
                xs={4}
                container
                flexDirection={'column'}
                alignItems={'flex-end'}
              >
                <Typography>
                  {moment(item.createdAt).format('DD.MM.YYYY')}
                </Typography>
                <Typography>
                  {moment(item.createdAt).format('HH:mm:ss')}
                </Typography>
              </Grid>
              {isSuperAdmin ? (
                <Grid
                  item
                  xs={2}
                  container
                  flexDirection={'column'}
                  alignItems={'flex-end'}
                >
                  <Button
                    color={item.banned ? 'success' : 'error'}
                    variant='contained'
                    onClick={() => updateUserSessions(item._id, !item.banned)}
                  >
                    {item.banned ? t('userModal.unblock') : t('userModal.block')}
                  </Button>
                </Grid>
              ) : null}
            </Grid>
          ))}
        </Grid>
        <Grid item display="flex" justifyContent="space-between" alignItems="center">
          {data.length ? (
            <Pagination
              count={pages}
              page={page}
              onChange={(event, nextPage) => setPage(nextPage)}
            />
          ) : null}
          {isSuperAdmin && data.length ? (
            <Button
              color='warning'
              variant='contained'
              onClick={() => onCloseSessions()}
            >
              {t('userModal.endSessions')}
            </Button>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserSessionsContent;
